import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Input, Label, Button } from '../components/ui';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Loader2, Eye, EyeOff, Lock, CheckCircle } from '../components/icons';

//const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5002';

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const { register, handleSubmit, watch, formState: { errors } } = useForm();
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [tokenValid, setTokenValid] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const password = watch('password');

  useEffect(() => {
    const verifyToken = async () => {
      if (!token) { setError('Token no proporcionado'); setVerifying(false); return; }
      try {
        const response = await fetch(`/api/password-reset/verify?token=${token}`);
        const result = await response.json();
        if (response.ok && result.valid) { setTokenValid(true); }
        else { setError(result.error || 'Token inválido o expirado'); }
      } catch (err) { setError('Error al verificar el token'); }
      finally { setVerifying(false); }
    };
    verifyToken();
  }, [token]);

  const onSubmit = async (data) => {
    setLoading(true); setError('');
    try {
      const response = await fetch(`/api/password-reset/reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, newPassword: data.password }),
      });
      const result = await response.json();
      if (response.ok) {
        setSuccess(true);
        setTimeout(() => navigate('/login'), 3000);
      } else { setError(result.error || 'Error al restablecer'); }
    } catch (err) { setError('Error de conexión'); }
    finally { setLoading(false); }
  };

  if (verifying) return (
    <div className="flex min-h-screen flex-1 flex-col justify-start pt-20 px-6 py-12 lg:px-8 bg-gray-50 dark:bg-gray-900">
      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-lg">
        <div className="bg-white dark:bg-gray-800 py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <div className="flex flex-col items-center space-y-4">
            <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
            <p className="text-gray-600 dark:text-gray-400">Verificando enlace...</p>
          </div>
        </div>
      </div>
    </div>
  );

  if (!tokenValid) return (
    <div className="flex min-h-screen flex-1 flex-col justify-start pt-20 px-6 py-12 lg:px-8 bg-gray-50 dark:bg-gray-900">
      <div className="sm:mx-auto sm:w-full sm:max-w-lg">
        <h2 className="mt-6 text-center text-2xl font-bold text-gray-900 dark:text-white">Enlace Inválido</h2>
      </div>
      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-lg">
        <div className="bg-white dark:bg-gray-800 py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <Alert variant="destructive" className="mb-4"><AlertDescription>{error}</AlertDescription></Alert>
          <Link to="/forgot-password" className="block text-center">
            <Button className="w-full">Solicitar nuevo enlace</Button>
          </Link>
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex min-h-screen flex-1 flex-col justify-start pt-20 px-6 py-12 lg:px-8 bg-gray-50 dark:bg-gray-900">
      <div className="sm:mx-auto sm:w-full sm:max-w-lg">
        <h2 className="mt-6 text-center text-2xl font-bold text-gray-900 dark:text-white">Nueva Contraseña</h2>
      </div>
      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-lg">
        <div className="bg-white dark:bg-gray-800 py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {success ? (
            <div className="space-y-6">
              <Alert className="bg-green-50"><CheckCircle className="h-4 w-4 text-green-600" />
                <AlertDescription>Contraseña restablecida. Redirigiendo...</AlertDescription>
              </Alert>
              <Link to="/login" className="block text-center">
                <Button className="w-full">Ir al Login</Button>
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}
              <div>
                <Label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Nueva Contraseña</Label>
                <div className="relative mt-2">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-gray-400" />
                  </div>
                  <Input id="password" type={showPassword ? 'text' : 'password'} className="block w-full pl-10 pr-10 py-2.5" {...register('password', { required: true, minLength: 6 })} />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600">
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>
              <div>
                <Label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Confirmar Contraseña</Label>
                <div className="relative mt-2">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-gray-400" />
                  </div>
                  <Input id="confirmPassword" type={showConfirmPassword ? 'text' : 'password'} className="block w-full pl-10 pr-10 py-2.5" {...register('confirmPassword', { validate: v => v === password || 'No coinciden' })} />
                  <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600">
                    {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
                {errors.confirmPassword && <p className="text-sm text-red-500">{errors.confirmPassword.message}</p>}
              </div>
              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Restableciendo...</> : 'Restablecer Contraseña'}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
